import React, { useState, useEffect } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../ui/tabs";
import { Badge } from "../ui/badge";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import {
  Building2,
  Users,
  Shield,
  TrendingUp,
  TrendingDown,
  Search,
  Filter,
  BarChart3,
  PieChart,
  Activity,
  Calendar,
  Download,
  Plus
} from "lucide-react";
import SubsidiaryHierarchyTree from "./SubsidiaryHierarchyTree";
import ComparisonChartsPanel from "./ComparisonChartsPanel";
import EditCompanyInfoDialog from "./EditCompanyInfoDialog";
import { useBusinessPlan } from "../../App";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api/enterprise`;

const EnhancedOverviewDashboard = ({ 
  companyInfo, 
  subsidiaries = [], 
  teamMembers = [], 
  roles = [],
  userRoles = [],
  onRefresh,
  onCreateSubsidiary,
  onAddTeamMember,
  onCreateRole,
  onCreateBusinessPlan,
  onViewAllSubsidiaries,
  onViewAllTeamMembers
}) => {
  const [activeSubTab, setActiveSubTab] = useState("summary");
  const [loading, setLoading] = useState(false);
  const [editCompanyDialogOpen, setEditCompanyDialogOpen] = useState(false);
  const [showCreatePlanDialog, setShowCreatePlanDialog] = useState(false);
  const [showComparisonModal, setShowComparisonModal] = useState(false);
  const { createNewPlan, setCurrentPlan } = useBusinessPlan();
  
  const [createPlanForm, setCreatePlanForm] = useState({
    name: "",
    company_name: "",
    currency: "USD",
    fiscal_year_start: 1
  });
  const [stats, setStats] = useState({
    totalSubsidiaries: 0,
    activeSubsidiaries: 0,
    totalTeamMembers: 0,
    activeTeamMembers: 0,
    totalRoles: 0,
    avgTeamSize: 0,
    topLevelSubsidiaries: 0,
    childSubsidiaries: 0
  });
  
  // Filters state
  const [filters, setFilters] = useState({
    searchTerm: "",
    status: "all", // all, active, inactive
    dateRange: "all", // all, last_30, last_90, last_year
    sortBy: "name" // name, team_size, created_date
  });

  useEffect(() => {
    calculateStats();
  }, [subsidiaries, teamMembers, roles, userRoles]);

  const calculateStats = () => {
    const activeSubsidiaries = subsidiaries.filter(s => s.is_active !== false);
    const activeTeamMembers = teamMembers.filter(m => m.is_active);
    const topLevel = subsidiaries.filter(s => !s.parent_id);
    const children = subsidiaries.filter(s => s.parent_id);
    
    const avgTeamSize = activeSubsidiaries.length > 0
      ? (activeTeamMembers.length / activeSubsidiaries.length).toFixed(1)
      : 0;

    setStats({
      totalSubsidiaries: subsidiaries.length,
      activeSubsidiaries: activeSubsidiaries.length,
      totalTeamMembers: teamMembers.length,
      activeTeamMembers: activeTeamMembers.length,
      totalRoles: roles.length,
      avgTeamSize: avgTeamSize,
      topLevelSubsidiaries: topLevel.length,
      childSubsidiaries: children.length
    });
  };

  const getGrowthPercentage = (current, previous) => {
    if (!previous) return 0;
    return (((current - previous) / previous) * 100).toFixed(1);
  };

  const renderDashboardTab = () => {
    return (
      <div className="space-y-6">
        {/* Company Info Card */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center justify-between">
              <span className="flex items-center gap-2">
                <Building2 className="h-5 w-5" />
                Company Information
              </span>
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => setEditCompanyDialogOpen(true)}
              >
                Edit Company Info
              </Button>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {/* Basic Info */}
              <div className="space-y-4">
                <h4 className="text-xs font-semibold text-gray-500 uppercase">Basic Information</h4>
                <div>
                  <label className="text-xs text-gray-500">Company Name</label>
                  <div className="mt-1 text-sm font-semibold">
                    {companyInfo?.company_name || "N/A"}
                  </div>
                </div>
                <div>
                  <label className="text-xs text-gray-500">Industry</label>
                  <div className="mt-1 text-sm">
                    {companyInfo?.industry || "Not specified"}
                  </div>
                </div>
                <div>
                  <label className="text-xs text-gray-500">Account Status</label>
                  <div className="mt-1">
                    <Badge variant="default" className="bg-green-100 text-green-700">
                      {companyInfo?.is_active ? "Active" : "Inactive"}
                    </Badge>
                  </div>
                </div>
              </div>

              {/* Contact Info */}
              <div className="space-y-4">
                <h4 className="text-xs font-semibold text-gray-500 uppercase">Contact Information</h4>
                <div>
                  <label className="text-xs text-gray-500">Contact Person</label>
                  <div className="mt-1 text-sm font-semibold">
                    {companyInfo?.full_name || "N/A"}
                  </div>
                </div>
                <div>
                  <label className="text-xs text-gray-500">Email</label>
                  <div className="mt-1 text-sm text-blue-600">
                    {companyInfo?.email || "N/A"}
                  </div>
                </div>
                <div>
                  <label className="text-xs text-gray-500">Phone</label>
                  <div className="mt-1 text-sm">
                    {companyInfo?.phone || "Not provided"}
                  </div>
                </div>
                <div>
                  <label className="text-xs text-gray-500">Website</label>
                  <div className="mt-1 text-sm text-blue-600">
                    {companyInfo?.website ? (
                      <a href={companyInfo.website} target="_blank" rel="noopener noreferrer" className="hover:underline">
                        {companyInfo.website}
                      </a>
                    ) : "Not provided"}
                  </div>
                </div>
              </div>

              {/* Location & Legal */}
              <div className="space-y-4">
                <h4 className="text-xs font-semibold text-gray-500 uppercase">Location & Legal</h4>
                <div>
                  <label className="text-xs text-gray-500">Address</label>
                  <div className="mt-1 text-sm">
                    {companyInfo?.address || "Not provided"}
                  </div>
                </div>
                <div>
                  <label className="text-xs text-gray-500">City, Country</label>
                  <div className="mt-1 text-sm">
                    {companyInfo?.city && companyInfo?.country 
                      ? `${companyInfo.city}, ${companyInfo.country}`
                      : companyInfo?.city || companyInfo?.country || "Not provided"}
                  </div>
                </div>
                <div>
                  <label className="text-xs text-gray-500">Tax ID</label>
                  <div className="mt-1 text-sm">
                    {companyInfo?.tax_id || "Not provided"}
                  </div>
                </div>
                <div>
                  <label className="text-xs text-gray-500">Total Subsidiaries</label>
                  <div className="mt-1 text-sm font-semibold">
                    {subsidiaries.length} ({subsidiaries.filter(s => s.is_active !== false).length} active)
                  </div>
                </div>
              </div>
            </div>

            {companyInfo?.description && (
              <div className="mt-6 pt-6 border-t">
                <label className="text-xs text-gray-500 uppercase font-semibold">Company Description</label>
                <p className="mt-2 text-sm text-gray-700">{companyInfo.description}</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Key Metrics Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {/* Total Subsidiaries */}
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Total Subsidiaries</p>
                  <p className="text-3xl font-bold text-blue-600">{stats.totalSubsidiaries}</p>
                  <p className="text-xs text-gray-400 mt-1">
                    {stats.activeSubsidiaries} active
                  </p>
                </div>
                <Building2 className="h-12 w-12 text-blue-200" />
              </div>
              <div className="mt-3 flex items-center text-xs">
                <Activity className="h-3 w-3 text-blue-500 mr-1" />
                <span className="text-blue-500">Organization</span>
              </div>
            </CardContent>
          </Card>

          {/* Team Members */}
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Team Members</p>
                  <p className="text-3xl font-bold text-green-600">{stats.totalTeamMembers}</p>
                  <p className="text-xs text-gray-400 mt-1">
                    {stats.activeTeamMembers} active
                  </p>
                </div>
                <Users className="h-12 w-12 text-green-200" />
              </div>
              <div className="mt-3 flex items-center text-xs">
                <Activity className="h-3 w-3 text-green-500 mr-1" />
                <span className="text-green-500">Workforce</span>
              </div>
            </CardContent>
          </Card>

          {/* Roles Assigned */}
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Total Roles</p>
                  <p className="text-3xl font-bold text-purple-600">{stats.totalRoles}</p>
                  <p className="text-xs text-gray-400 mt-1">
                    {userRoles.length} assignments
                  </p>
                </div>
                <Shield className="h-12 w-12 text-purple-200" />
              </div>
              <div className="mt-3 flex items-center text-xs">
                <Activity className="h-3 w-3 text-purple-500 mr-1" />
                <span className="text-purple-500">Active</span>
              </div>
            </CardContent>
          </Card>

          {/* Avg Team Size */}
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Avg Team Size</p>
                  <p className="text-3xl font-bold text-orange-600">{stats.avgTeamSize}</p>
                  <p className="text-xs text-gray-400 mt-1">
                    per subsidiary
                  </p>
                </div>
                <BarChart3 className="h-12 w-12 text-orange-200" />
              </div>
              <div className="mt-3 flex items-center text-xs">
                <TrendingUp className="h-3 w-3 text-orange-500 mr-1" />
                <span className="text-orange-500">Optimal</span>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Hierarchy Breakdown */}
        <Card>
          <CardHeader>
            <CardTitle>Organizational Hierarchy</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-3">
                <div className="flex items-center justify-between p-3 bg-blue-50 rounded-lg">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-blue-600 rounded-full flex items-center justify-center">
                      <Building2 className="h-5 w-5 text-white" />
                    </div>
                    <div>
                      <p className="text-sm font-medium">Top-Level Subsidiaries</p>
                      <p className="text-xs text-gray-500">Parent organizations</p>
                    </div>
                  </div>
                  <span className="text-2xl font-bold text-blue-600">
                    {stats.topLevelSubsidiaries}
                  </span>
                </div>

                <div className="flex items-center justify-between p-3 bg-purple-50 rounded-lg">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-purple-600 rounded-full flex items-center justify-center">
                      <Building2 className="h-5 w-5 text-white" />
                    </div>
                    <div>
                      <p className="text-sm font-medium">Child Subsidiaries</p>
                      <p className="text-xs text-gray-500">Sub-organizations</p>
                    </div>
                  </div>
                  <span className="text-2xl font-bold text-purple-600">
                    {stats.childSubsidiaries}
                  </span>
                </div>
              </div>

              <div className="flex items-center justify-center">
                <div className="text-center">
                  <PieChart className="h-24 w-24 text-gray-300 mx-auto mb-3" />
                  <p className="text-sm text-gray-500">Hierarchy visualization</p>
                  <p className="text-xs text-gray-400">View in Hierarchy tab</p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Quick Actions */}
        <Card>
          <CardHeader>
            <CardTitle>Quick Actions</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Button 
                variant="outline" 
                className="justify-start h-auto py-4"
                onClick={onCreateSubsidiary}
              >
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                    <Building2 className="h-5 w-5 text-blue-600" />
                  </div>
                  <div className="text-left">
                    <div className="font-medium">Create Subsidiary</div>
                    <div className="text-xs text-gray-500">Add new branch or office</div>
                  </div>
                </div>
              </Button>
              
              <Button 
                variant="outline" 
                className="justify-start h-auto py-4"
                onClick={onAddTeamMember}
              >
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                    <Users className="h-5 w-5 text-green-600" />
                  </div>
                  <div className="text-left">
                    <div className="font-medium">Add Team Member</div>
                    <div className="text-xs text-gray-500">Invite or add users</div>
                  </div>
                </div>
              </Button>
              
              <Button 
                variant="outline" 
                className="justify-start h-auto py-4"
                onClick={onCreateRole}
              >
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                    <Shield className="h-5 w-5 text-purple-600" />
                  </div>
                  <div className="text-left">
                    <div className="font-medium">Create Role</div>
                    <div className="text-xs text-gray-500">Define permissions</div>
                  </div>
                </div>
              </Button>
              
              <Button 
                variant="outline" 
                className="justify-start h-auto py-4"
                onClick={() => setShowCreatePlanDialog(true)}
              >
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center">
                    <PieChart className="h-5 w-5 text-orange-600" />
                  </div>
                  <div className="text-left">
                    <div className="font-medium">Create Business Plan</div>
                    <div className="text-xs text-gray-500">Start strategic planning</div>
                  </div>
                </div>
              </Button>
              
              <Button 
                variant="outline" 
                className="justify-start h-auto py-4"
                onClick={() => setActiveSubTab("analytics")}
              >
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-indigo-100 rounded-lg flex items-center justify-center">
                    <BarChart3 className="h-5 w-5 text-indigo-600" />
                  </div>
                  <div className="text-left">
                    <div className="font-medium">View Reports</div>
                    <div className="text-xs text-gray-500">Analytics & insights</div>
                  </div>
                </div>
              </Button>
              
              <Button 
                variant="outline" 
                className="justify-start h-auto py-4"
                onClick={() => setEditCompanyDialogOpen(true)}
              >
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center">
                    <Activity className="h-5 w-5 text-gray-600" />
                  </div>
                  <div className="text-left">
                    <div className="font-medium">Company Settings</div>
                    <div className="text-xs text-gray-500">Edit company info</div>
                  </div>
                </div>
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Recent Activity */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center justify-between">
              <span className="flex items-center gap-2">
                <Activity className="h-5 w-5" />
                Recent Activity
              </span>
              <Button variant="outline" size="sm">
                View All
              </Button>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {subsidiaries.slice(0, 4).map((sub) => (
                <div key={sub.id} className="flex items-start gap-3 p-3 border rounded-lg hover:bg-gray-50">
                  <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <Building2 className="h-4 w-4 text-blue-600" />
                  </div>
                  <div className="flex-1">
                    <p className="text-sm font-medium">Subsidiary "{sub.name}" {sub.is_active !== false ? "is active" : "archived"}</p>
                    <p className="text-xs text-gray-500">
                      Created {new Date(sub.created_at).toLocaleDateString()}
                    </p>
                  </div>
                </div>
              ))}
              
              {subsidiaries.length === 0 && (
                <div className="text-center py-8 text-gray-500">
                  <Activity className="h-12 w-12 mx-auto mb-3 text-gray-300" />
                  <p className="text-sm">No recent activity</p>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    );
  };

  const renderOrganizationTab = () => {
    const [subsidiaryFilters, setSubsidiaryFilters] = useState({
      status: "all",
      location: "all",
      teamSize: "all",
      hierarchyLevel: "all"
    });

    const filteredSubsidiaries = subsidiaries.filter(sub => {
      if (subsidiaryFilters.status === "active" && sub.is_active === false) return false;
      if (subsidiaryFilters.status === "inactive" && sub.is_active !== false) return false;
      return true;
    });

    return (
      <div className="space-y-6">
        {/* Filters */}
        <Card>
          <CardContent className="pt-6">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div>
                <Label className="text-sm font-medium mb-2 block">Status</Label>
                <Select
                  value={subsidiaryFilters.status}
                  onValueChange={(value) => setSubsidiaryFilters({ ...subsidiaryFilters, status: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Status</SelectItem>
                    <SelectItem value="active">Active Only</SelectItem>
                    <SelectItem value="inactive">Inactive Only</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label className="text-sm font-medium mb-2 block">Location</Label>
                <Select
                  value={subsidiaryFilters.location}
                  onValueChange={(value) => setSubsidiaryFilters({ ...subsidiaryFilters, location: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Locations</SelectItem>
                    <SelectItem value="africa">Africa</SelectItem>
                    <SelectItem value="middle_east">Middle East</SelectItem>
                    <SelectItem value="asia">Asia</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label className="text-sm font-medium mb-2 block">Team Size</Label>
                <Select
                  value={subsidiaryFilters.teamSize}
                  onValueChange={(value) => setSubsidiaryFilters({ ...subsidiaryFilters, teamSize: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Sizes</SelectItem>
                    <SelectItem value="small">Small (1-5)</SelectItem>
                    <SelectItem value="medium">Medium (6-15)</SelectItem>
                    <SelectItem value="large">Large (16+)</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label className="text-sm font-medium mb-2 block">Hierarchy Level</Label>
                <Select
                  value={subsidiaryFilters.hierarchyLevel}
                  onValueChange={(value) => setSubsidiaryFilters({ ...subsidiaryFilters, hierarchyLevel: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Levels</SelectItem>
                    <SelectItem value="top">Top Level</SelectItem>
                    <SelectItem value="child">Child Level</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Interactive Hierarchy Tree */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center justify-between">
              <span>Interactive Hierarchy Tree</span>
              <Button variant="outline" size="sm">
                <Download className="h-4 w-4 mr-2" />
                Export
              </Button>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <SubsidiaryHierarchyTree subsidiaries={filteredSubsidiaries} />
          </CardContent>
        </Card>

        {/* Subsidiary Comparison Table */}
        <Card>
          <CardHeader>
            <CardTitle>Subsidiary Comparison Table</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="border-b">
                  <tr>
                    <th className="text-left py-3 px-4 text-sm font-medium text-gray-500">Name</th>
                    <th className="text-center py-3 px-4 text-sm font-medium text-gray-500">Team</th>
                    <th className="text-center py-3 px-4 text-sm font-medium text-gray-500">Plans</th>
                    <th className="text-center py-3 px-4 text-sm font-medium text-gray-500">Revenue</th>
                    <th className="text-center py-3 px-4 text-sm font-medium text-gray-500">Status</th>
                    <th className="text-center py-3 px-4 text-sm font-medium text-gray-500">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredSubsidiaries.slice(0, 10).map((sub) => {
                    return (
                      <tr key={sub.id} className="border-b hover:bg-gray-50">
                        <td className="py-3 px-4">
                          <div className="flex items-center gap-2">
                            <Building2 className="h-4 w-4 text-gray-400" />
                            <div>
                              <div className="font-medium">{sub.name}</div>
                              {sub.location && (
                                <div className="text-xs text-gray-500">{sub.location}</div>
                              )}
                            </div>
                          </div>
                        </td>
                        <td className="py-3 px-4 text-center">
                          <Badge variant="outline">{sub.team_count || 0}</Badge>
                        </td>
                        <td className="py-3 px-4 text-center">
                          <Badge variant="outline">-</Badge>
                        </td>
                        <td className="py-3 px-4 text-center font-medium text-gray-400">
                          -
                        </td>
                        <td className="py-3 px-4 text-center">
                          <Badge
                            variant={sub.is_active !== false ? "default" : "secondary"}
                            className={sub.is_active !== false ? "bg-green-100 text-green-700" : ""}
                          >
                            {sub.is_active !== false ? "✅ Active" : "⚠️ Archived"}
                          </Badge>
                        </td>
                        <td className="py-3 px-4 text-center">
                          <Button 
                            variant="ghost" 
                            size="sm"
                            onClick={() => setShowComparisonModal(true)}
                          >
                            View
                          </Button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  };

  const renderTeamTab = () => {
    // Calculate team distribution by role
    const roleDistribution = {};
    userRoles.forEach(ur => {
      const role = roles.find(r => r.id === ur.role_id);
      if (role) {
        roleDistribution[role.name] = (roleDistribution[role.name] || 0) + 1;
      }
    });

    // Calculate team distribution by subsidiary
    const subsidiaryDistribution = {};
    subsidiaries.forEach(sub => {
      subsidiaryDistribution[sub.name] = sub.team_count || 0;
    });

    return (
      <div className="space-y-6">
        {/* Team Distribution Charts */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Total Team</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-center">
                <div className="text-4xl font-bold text-blue-600 mb-2">
                  {stats.totalTeamMembers}
                </div>
                <p className="text-sm text-gray-500">Active Members</p>
                <div className="mt-4">
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div
                      className="bg-blue-600 h-2 rounded-full"
                      style={{ width: `${(stats.activeTeamMembers / stats.totalTeamMembers * 100)}%` }}
                    ></div>
                  </div>
                  <p className="text-xs text-gray-500 mt-1">
                    {stats.activeTeamMembers} active of {stats.totalTeamMembers}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-base">By Role</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {Object.entries(roleDistribution).slice(0, 4).map(([roleName, count]) => (
                  <div key={roleName} className="flex items-center justify-between">
                    <span className="text-sm text-gray-600">{roleName}</span>
                    <Badge variant="outline">{count}</Badge>
                  </div>
                ))}
                {Object.keys(roleDistribution).length > 4 && (
                  <Button variant="link" size="sm" className="w-full">
                    View All Roles
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-base">By Subsidiary</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {Object.entries(subsidiaryDistribution).slice(0, 4).map(([subName, count]) => (
                  <div key={subName} className="flex items-center justify-between">
                    <span className="text-sm text-gray-600 truncate">{subName}</span>
                    <Badge variant="outline">{count}</Badge>
                  </div>
                ))}
                {Object.keys(subsidiaryDistribution).length > 4 && (
                  <Button variant="link" size="sm" className="w-full">
                    View All Subsidiaries
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Team Distribution Details */}
        <Card>
          <CardHeader>
            <CardTitle>Team Distribution by Role</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {Object.keys(roleDistribution).length > 0 ? (
                <>
                  {/* Calculate total role assignments */}
                  {(() => {
                    const totalRoleAssignments = Object.values(roleDistribution).reduce((sum, count) => sum + count, 0);
                    
                    return (
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {Object.entries(roleDistribution).map(([roleName, count], idx) => {
                          const colors = [
                            { bg: 'bg-blue-100', text: 'text-blue-700', bar: 'bg-blue-600' },
                            { bg: 'bg-green-100', text: 'text-green-700', bar: 'bg-green-600' },
                            { bg: 'bg-purple-100', text: 'text-purple-700', bar: 'bg-purple-600' },
                            { bg: 'bg-orange-100', text: 'text-orange-700', bar: 'bg-orange-600' },
                            { bg: 'bg-pink-100', text: 'text-pink-700', bar: 'bg-pink-600' },
                            { bg: 'bg-indigo-100', text: 'text-indigo-700', bar: 'bg-indigo-600' },
                            { bg: 'bg-gray-100', text: 'text-gray-700', bar: 'bg-gray-600' }
                          ];
                          const color = colors[idx % colors.length];
                          
                          // Calculate percentage based on total role assignments
                          const percentage = totalRoleAssignments > 0 
                            ? Math.min((count / totalRoleAssignments * 100), 100).toFixed(1)
                            : 0;

                          return (
                            <div key={roleName} className="p-4 border rounded-lg">
                              <div className="flex items-center justify-between mb-3">
                                <span className="text-sm font-medium text-gray-700">{roleName}</span>
                                <Badge className={`${color.bg} ${color.text}`}>
                                  {count}
                                </Badge>
                              </div>
                              <div className="w-full bg-gray-200 rounded-full h-2 overflow-hidden">
                                <div
                                  className={`${color.bar} h-2 rounded-full transition-all`}
                                  style={{ width: `${percentage}%`, maxWidth: '100%' }}
                                ></div>
                              </div>
                              <p className="text-xs text-gray-500 mt-1">{percentage}% of role assignments</p>
                            </div>
                          );
                        })}
                      </div>
                    );
                  })()}
                </>
              ) : (
                <div className="text-center py-8 text-gray-500">
                  <p>No team members assigned to roles yet</p>
                </div>
              )}

              <div className="pt-4 border-t">
                <Button 
                  variant="outline" 
                  className="w-full"
                  onClick={() => {
                    if (onViewAllTeamMembers) {
                      onViewAllTeamMembers();
                    }
                  }}
                >
                  View Detailed Breakdown
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  };

  const renderAnalyticsTab = () => {
    const [timeRange, setTimeRange] = useState("last_30");
    const [compareWith, setCompareWith] = useState("previous");

    const filteredSubsidiaries = subsidiaries.filter(sub => {
      // Search filter
      if (filters.searchTerm) {
        const searchLower = filters.searchTerm.toLowerCase();
        if (!sub.name?.toLowerCase().includes(searchLower) && 
            !sub.location?.toLowerCase().includes(searchLower)) {
          return false;
        }
      }

      // Status filter
      if (filters.status === "active" && sub.is_active === false) return false;
      if (filters.status === "inactive" && sub.is_active !== false) return false;

      return true;
    });

    return (
      <div className="space-y-6">
        {/* Time Range Selector */}
        <Card>
          <CardContent className="pt-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label className="text-sm font-medium mb-2 block">Time Range</Label>
                <Select value={timeRange} onValueChange={setTimeRange}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="last_7">Last 7 Days</SelectItem>
                    <SelectItem value="last_30">Last 30 Days</SelectItem>
                    <SelectItem value="last_90">Last 90 Days</SelectItem>
                    <SelectItem value="last_year">Last Year</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label className="text-sm font-medium mb-2 block">Compare to</Label>
                <Select value={compareWith} onValueChange={setCompareWith}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="previous">Previous Period</SelectItem>
                    <SelectItem value="last_year">Same Period Last Year</SelectItem>
                    <SelectItem value="baseline">Baseline</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Growth Trends */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <TrendingUp className="h-5 w-5" />
              Growth Trends
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="h-64 flex items-center justify-center border-2 border-dashed rounded-lg">
                <div className="text-center">
                  <BarChart3 className="h-12 w-12 text-gray-300 mx-auto mb-3" />
                  <p className="text-sm text-gray-500">Line chart showing subsidiary growth over time</p>
                  <p className="text-xs text-gray-400 mt-1">Visualization coming soon</p>
                </div>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="p-4 border rounded-lg">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm text-gray-600">Total Subsidiaries</span>
                    <Building2 className="h-4 w-4 text-blue-500" />
                  </div>
                  <div className="text-2xl font-bold text-blue-600">{stats.totalSubsidiaries}</div>
                  <p className="text-xs text-gray-500">{stats.activeSubsidiaries} active</p>
                </div>

                <div className="p-4 border rounded-lg">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm text-gray-600">Total Team Members</span>
                    <Users className="h-4 w-4 text-green-500" />
                  </div>
                  <div className="text-2xl font-bold text-green-600">{stats.totalTeamMembers}</div>
                  <p className="text-xs text-gray-500">{stats.activeTeamMembers} active</p>
                </div>

                <div className="p-4 border rounded-lg">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm text-gray-600">Average Team Size</span>
                    <Activity className="h-4 w-4 text-purple-500" />
                  </div>
                  <div className="text-2xl font-bold text-purple-600">{stats.avgTeamSize}</div>
                  <p className="text-xs text-gray-500">members per subsidiary</p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Performance Comparison */}
        <Card>
          <CardHeader>
            <CardTitle>Performance Comparison</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-64 flex items-center justify-center border-2 border-dashed rounded-lg">
              <div className="text-center">
                <PieChart className="h-12 w-12 text-gray-300 mx-auto mb-3" />
                <p className="text-sm text-gray-500">Bar chart comparing subsidiaries by key metrics</p>
                <p className="text-xs text-gray-400 mt-1">Visualization coming soon</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Key Insights */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Activity className="h-5 w-5" />
              Key Insights
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {/* Top performing subsidiary by team size */}
              {(() => {
                const topSubsidiary = [...subsidiaries]
                  .filter(s => s.is_active !== false)
                  .sort((a, b) => (b.team_count || 0) - (a.team_count || 0))[0];
                
                if (topSubsidiary) {
                  return (
                    <div className="flex items-start gap-3 p-4 bg-green-50 border border-green-200 rounded-lg">
                      <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
                        <TrendingUp className="h-4 w-4 text-green-600" />
                      </div>
                      <div>
                        <p className="font-medium text-green-900">Largest team: {topSubsidiary.name}</p>
                        <p className="text-sm text-green-700">{topSubsidiary.team_count || 0} team members assigned</p>
                      </div>
                    </div>
                  );
                }
              })()}

              {/* Subsidiaries needing attention */}
              {(() => {
                const needsAttention = subsidiaries.filter(s => s.is_active !== false && (s.team_count || 0) === 0);
                
                if (needsAttention.length > 0) {
                  return (
                    <div className="flex items-start gap-3 p-4 bg-orange-50 border border-orange-200 rounded-lg">
                      <div className="w-8 h-8 bg-orange-100 rounded-full flex items-center justify-center flex-shrink-0">
                        <Activity className="h-4 w-4 text-orange-600" />
                      </div>
                      <div>
                        <p className="font-medium text-orange-900">Needs attention: {needsAttention.length} {needsAttention.length === 1 ? 'subsidiary' : 'subsidiaries'}</p>
                        <p className="text-sm text-orange-700">No team members assigned yet - {needsAttention.map(s => s.name).slice(0, 2).join(', ')}</p>
                      </div>
                    </div>
                  );
                }
              })()}

              {/* Total workforce insight */}
              <div className="flex items-start gap-3 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <Users className="h-4 w-4 text-blue-600" />
                </div>
                <div>
                  <p className="font-medium text-blue-900">Total workforce: {stats.totalTeamMembers} members</p>
                  <p className="text-sm text-blue-700">
                    {stats.activeTeamMembers} active across {stats.activeSubsidiaries} subsidiaries
                  </p>
                </div>
              </div>

              {/* Role distribution insight */}
              <div className="flex items-start gap-3 p-4 bg-purple-50 border border-purple-200 rounded-lg">
                <div className="w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <Shield className="h-4 w-4 text-purple-600" />
                </div>
                <div>
                  <p className="font-medium text-purple-900">Role distribution: {stats.totalRoles} roles defined</p>
                  <p className="text-sm text-purple-700">{userRoles.length} total role assignments across the organization</p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Filters Bar */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Filter className="h-5 w-5" />
              Advanced Filters
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div>
                <Label className="text-sm font-medium mb-2 block">Search</Label>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <Input
                    placeholder="Search subsidiaries..."
                    value={filters.searchTerm}
                    onChange={(e) => setFilters({ ...filters, searchTerm: e.target.value })}
                    className="pl-10"
                  />
                </div>
              </div>

              <div>
                <Label className="text-sm font-medium mb-2 block">Status</Label>
                <Select
                  value={filters.status}
                  onValueChange={(value) => setFilters({ ...filters, status: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Status</SelectItem>
                    <SelectItem value="active">Active Only</SelectItem>
                    <SelectItem value="inactive">Inactive Only</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label className="text-sm font-medium mb-2 block">Date Range</Label>
                <Select
                  value={filters.dateRange}
                  onValueChange={(value) => setFilters({ ...filters, dateRange: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Time</SelectItem>
                    <SelectItem value="last_30">Last 30 Days</SelectItem>
                    <SelectItem value="last_90">Last 90 Days</SelectItem>
                    <SelectItem value="last_year">Last Year</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label className="text-sm font-medium mb-2 block">Sort By</Label>
                <Select
                  value={filters.sortBy}
                  onValueChange={(value) => setFilters({ ...filters, sortBy: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="name">Name</SelectItem>
                    <SelectItem value="team_size">Team Size</SelectItem>
                    <SelectItem value="created_date">Created Date</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Analytics Results */}
        <Card>
          <CardHeader>
            <CardTitle>
              Analytics Results ({filteredSubsidiaries.length} subsidiaries)
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {filteredSubsidiaries.map((sub) => (
                <div key={sub.id} className="flex items-center justify-between p-4 border rounded-lg hover:bg-gray-50">
                  <div className="flex items-center gap-4">
                    <Building2 className="h-8 w-8 text-blue-500" />
                    <div>
                      <p className="font-medium">{sub.name}</p>
                      <p className="text-sm text-gray-500">
                        {sub.location || "No location"} • Team: {sub.team_count || 0}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge variant={sub.is_active !== false ? "default" : "secondary"}>
                      {sub.is_active !== false ? "Active" : "Inactive"}
                    </Badge>
                  </div>
                </div>
              ))}

              {filteredSubsidiaries.length === 0 && (
                <div className="text-center py-12">
                  <Search className="h-12 w-12 text-gray-300 mx-auto mb-3" />
                  <p className="text-gray-500">No subsidiaries match your filters</p>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    );
  };

  return (
    <div className="space-y-6">
      <Tabs value={activeSubTab} onValueChange={setActiveSubTab}>
        <TabsList className="grid w-full grid-cols-4">
          <TabsTrigger value="dashboard">
            <BarChart3 className="h-4 w-4 mr-2" />
            Dashboard
          </TabsTrigger>
          <TabsTrigger value="organization">
            <Building2 className="h-4 w-4 mr-2" />
            Organization
          </TabsTrigger>
          <TabsTrigger value="team">
            <Users className="h-4 w-4 mr-2" />
            Team
          </TabsTrigger>
          <TabsTrigger value="analytics">
            <Activity className="h-4 w-4 mr-2" />
            Analytics
          </TabsTrigger>
        </TabsList>

        <TabsContent value="dashboard">{renderDashboardTab()}</TabsContent>
        <TabsContent value="organization">{renderOrganizationTab()}</TabsContent>
        <TabsContent value="team">{renderTeamTab()}</TabsContent>
        <TabsContent value="analytics">{renderAnalyticsTab()}</TabsContent>
      </Tabs>

      {/* Edit Company Info Dialog */}
      <EditCompanyInfoDialog
        open={editCompanyDialogOpen}
        onClose={() => setEditCompanyDialogOpen(false)}
        companyInfo={companyInfo}
        onSuccess={() => {
          setEditCompanyDialogOpen(false);
          onRefresh && onRefresh();
        }}
      />

      {/* Create Business Plan Dialog */}
      <Dialog open={showCreatePlanDialog} onOpenChange={setShowCreatePlanDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Create Business Plan</DialogTitle>
          </DialogHeader>
          <form onSubmit={async (e) => {
            e.preventDefault();
            if (!createPlanForm.name || !createPlanForm.company_name) {
              toast.error("Please fill in all required fields");
              return;
            }
            setLoading(true);
            try {
              const newPlan = await createNewPlan(createPlanForm);
              if (newPlan) {
                toast.success("Business plan created successfully!");
                setCurrentPlan(newPlan);
                setShowCreatePlanDialog(false);
                setCreatePlanForm({ name: "", company_name: "", currency: "USD", fiscal_year_start: 1 });
              }
            } catch (error) {
              console.error("Error creating business plan:", error);
              toast.error(error.response?.data?.detail || "Failed to create business plan");
            } finally {
              setLoading(false);
            }
          }} className="space-y-4 py-4">
            <div>
              <Label htmlFor="plan-name">Plan Name *</Label>
              <Input
                id="plan-name"
                value={createPlanForm.name}
                onChange={(e) => setCreatePlanForm({...createPlanForm, name: e.target.value})}
                placeholder="e.g., 2025 Strategic Plan"
                required
              />
            </div>
            <div>
              <Label htmlFor="company-name">Company Name / Subsidiary *</Label>
              <Select
                value={createPlanForm.company_name}
                onValueChange={(value) => setCreatePlanForm({...createPlanForm, company_name: value})}
              >
                <SelectTrigger id="company-name">
                  <SelectValue placeholder="Select company or subsidiary" />
                </SelectTrigger>
                <SelectContent>
                  {/* Company as first option */}
                  <SelectItem value={companyInfo?.company_name || "Company"}>
                    {companyInfo?.company_name || "Company"} (Main)
                  </SelectItem>
                  {/* List all subsidiaries */}
                  {subsidiaries.filter(s => s.is_active !== false).map((sub) => (
                    <SelectItem key={sub.id} value={sub.name}>
                      {sub.name} {sub.location && `(${sub.location})`}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-gray-500 mt-1">
                Select the company or subsidiary this plan is for
              </p>
            </div>
            <div>
              <Label htmlFor="currency">Currency</Label>
              <Select
                value={createPlanForm.currency}
                onValueChange={(value) => setCreatePlanForm({...createPlanForm, currency: value})}
              >
                <SelectTrigger id="currency">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="USD">USD - US Dollar</SelectItem>
                  <SelectItem value="EUR">EUR - Euro</SelectItem>
                  <SelectItem value="GBP">GBP - British Pound</SelectItem>
                  <SelectItem value="JPY">JPY - Japanese Yen</SelectItem>
                  <SelectItem value="CAD">CAD - Canadian Dollar</SelectItem>
                  <SelectItem value="AUD">AUD - Australian Dollar</SelectItem>
                  <SelectItem value="INR">INR - Indian Rupee</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="fiscal-year">Fiscal Year Start</Label>
              <Select
                value={createPlanForm.fiscal_year_start.toString()}
                onValueChange={(value) => setCreatePlanForm({...createPlanForm, fiscal_year_start: parseInt(value)})}
              >
                <SelectTrigger id="fiscal-year">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Array.from({ length: 12 }, (_, i) => i + 1).map((month) => (
                    <SelectItem key={month} value={month.toString()}>
                      {new Date(2024, month - 1).toLocaleString('default', { month: 'long' })}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex justify-end gap-2 pt-4 border-t">
              <Button type="button" variant="outline" onClick={() => setShowCreatePlanDialog(false)} disabled={loading}>
                Cancel
              </Button>
              <Button type="submit" disabled={loading}>
                {loading ? "Creating..." : "Create Plan"}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Detailed Comparison Modal */}
      <Dialog open={showComparisonModal} onOpenChange={setShowComparisonModal}>
        <DialogContent className="max-w-6xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Detailed Subsidiary Comparison</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <div className="grid grid-cols-1 gap-6">
              {/* Comparison Table with More Details */}
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="py-3 px-4 text-left font-medium text-gray-700">Subsidiary</th>
                      <th className="py-3 px-4 text-center font-medium text-gray-700">Location</th>
                      <th className="py-3 px-4 text-center font-medium text-gray-700">Type</th>
                      <th className="py-3 px-4 text-center font-medium text-gray-700">Team Size</th>
                      <th className="py-3 px-4 text-center font-medium text-gray-700">Active Plans</th>
                      <th className="py-3 px-4 text-center font-medium text-gray-700">Status</th>
                      <th className="py-3 px-4 text-center font-medium text-gray-700">Parent</th>
                      <th className="py-3 px-4 text-center font-medium text-gray-700">Children</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {subsidiaries.map((sub, idx) => {
                      const teamSize = teamMembers.filter(m => 
                        userRoles.some(ur => ur.user_id === m.id && ur.subsidiary_id === sub.id)
                      ).length;
                      const childCount = subsidiaries.filter(s => s.parent_id === sub.id).length;
                      const parent = subsidiaries.find(s => s.id === sub.parent_id);
                      
                      return (
                        <tr key={idx} className="hover:bg-gray-50">
                          <td className="py-3 px-4">
                            <div className="font-medium text-gray-900">{sub.name}</div>
                            {sub.description && (
                              <div className="text-xs text-gray-500 mt-1">{sub.description.substring(0, 50)}...</div>
                            )}
                          </td>
                          <td className="py-3 px-4 text-center text-sm">
                            {sub.location || "-"}
                          </td>
                          <td className="py-3 px-4 text-center">
                            <Badge variant="outline" className="text-xs">
                              {sub.subsidiary_type || "Branch"}
                            </Badge>
                          </td>
                          <td className="py-3 px-4 text-center text-sm font-medium">
                            {teamSize}
                          </td>
                          <td className="py-3 px-4 text-center">
                            <Badge variant="secondary" className="text-xs">
                              0 {/* Will be calculated from plans */}
                            </Badge>
                          </td>
                          <td className="py-3 px-4 text-center">
                            <Badge 
                              variant={sub.is_active !== false ? "default" : "secondary"}
                              className={sub.is_active !== false ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-700"}
                            >
                              {sub.is_active !== false ? "Active" : "Inactive"}
                            </Badge>
                          </td>
                          <td className="py-3 px-4 text-center text-sm">
                            {parent ? (
                              <span className="text-blue-600">{parent.name}</span>
                            ) : (
                              <span className="text-gray-400">-</span>
                            )}
                          </td>
                          <td className="py-3 px-4 text-center text-sm font-medium">
                            {childCount > 0 ? (
                              <span className="text-purple-600">{childCount}</span>
                            ) : (
                              <span className="text-gray-400">0</span>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {/* Summary Statistics */}
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4 pt-4 border-t">
                <Card>
                  <CardContent className="p-4">
                    <div className="text-center">
                      <p className="text-2xl font-bold text-blue-600">{subsidiaries.length}</p>
                      <p className="text-xs text-gray-500 mt-1">Total Subsidiaries</p>
                    </div>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-4">
                    <div className="text-center">
                      <p className="text-2xl font-bold text-green-600">
                        {subsidiaries.filter(s => s.is_active !== false).length}
                      </p>
                      <p className="text-xs text-gray-500 mt-1">Active</p>
                    </div>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-4">
                    <div className="text-center">
                      <p className="text-2xl font-bold text-purple-600">
                        {subsidiaries.filter(s => !s.parent_id).length}
                      </p>
                      <p className="text-xs text-gray-500 mt-1">Top-Level</p>
                    </div>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-4">
                    <div className="text-center">
                      <p className="text-2xl font-bold text-orange-600">
                        {[...new Set(subsidiaries.map(s => s.location).filter(Boolean))].length}
                      </p>
                      <p className="text-xs text-gray-500 mt-1">Locations</p>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>
          </div>
          <div className="flex justify-end gap-2 pt-4 border-t">
            <Button variant="outline" onClick={() => setShowComparisonModal(false)}>
              Close
            </Button>
            <Button onClick={() => {
              setShowComparisonModal(false);
              if (onViewAllSubsidiaries) onViewAllSubsidiaries();
            }}>
              Go to Subsidiaries Tab
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// Add Label import
const Label = ({ children, className = "", ...props }) => (
  <label className={`text-sm font-medium ${className}`} {...props}>
    {children}
  </label>
);

export default EnhancedOverviewDashboard;
